import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft } from "lucide-react";

export type PageHeaderProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    title?: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    showDivider?: boolean;
    onBackClick?: () => void;
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

const PageHeader = <ClassName extends string = string>({
  title,
  description,
  actions,
  children,
  showDivider = true,
  onBackClick,
  className,
  UNSAFE_className,
}: PageHeaderProps<ClassName>) => {
  return (
    <div
      className={twMerge(
        "w-full flex flex-col gap-4",
        className,
        UNSAFE_className,
      )}
    >
      <div className="flex flex-col gap-4">
        {onBackClick && (
          <div className="flex justify-between items-center">
            <Button
              onClick={onBackClick}
              variant="tertiary"
              size="base"
              startEnhancer={<ArrowLeft />}
              UNSAFE_className="px-0 hover:bg-transparent hover:text-default/60 gap-1"
            >
              Back
            </Button>
            {actions && (
              <div className="flex gap-3 items-center">{actions}</div>
            )}
          </div>
        )}

        <div className="flex justify-between items-start gap-6">
          <div className="flex flex-col gap-2">
            {title &&
              (typeof title === "string" ? (
                <span className="header-h3 text-default break-words">
                  {title}
                </span>
              ) : (
                title
              ))}
            {description && (
              <span className="text-sm text-muted break-words leading-relaxed">
                {description}
              </span>
            )}
          </div>

          {!onBackClick && actions && (
            <div className="flex gap-3 items-center">{actions}</div>
          )}
        </div>
      </div>

      {children}

      {showDivider && <Separator />}
    </div>
  );
};

export default PageHeader;
